import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, InfinityIcon, GiftIcon } from 'lucide-react';
import { QuantityStepper, money } from './QuantityStepper';
import { GoldMedal } from './LifetimeSeal';
import { PrescriptionBadge } from './PrescriptionBadge';
import { offers, Offer } from '../data/product';
import { cartLink, variantIdOf, PRODUCT_LINKS } from '../data/productLinks';
import { Link } from 'react-router-dom';
import { CompanyPack } from './CompanyPack';
import { IncludedGear } from './IncludedGear';
import { UpsellModal } from './UpsellModal';
import { LifetimeUpgradeOption } from './LifetimeUpgradeOption';
import { useUpsellFlow } from '../hooks/useUpsellFlow';
import { buildFlow, checkoutFlow, suggestionFor, PackSuggestion, LIFETIME_UPGRADE_PRICE, SINGLE_PRICE, SINGLE_WITH_LIFETIME_PRICE } from '../data/upsellFlows';
import { requestBusinessQuantity, scrollToPackCta } from '../utils/offerBus';
import { flowSeen, setSelectionSource, SelectionSource } from '../utils/funnelState';
import { PaymentLogos } from './PaymentLogos';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { trackCustom, packEvents, trackShopifyAddToCart } from '../utils/tracking';
import { PRODUCT_NAME, warrantyCopy, pricing } from '../data/promotion';
import { useActiveSale } from '../hooks/useActiveSale';
import { RxQuantityControl } from './RxQuantityControl';
import { CareKitControl } from './CareKitControl';
import { setPendingCare, setPendingRx } from '../utils/cartAddons';
interface OfferSectionProps {
  selectedOffer: Offer['id'];
  onSelectOffer: (id: Offer['id']) => void;
}
const packMath: Record<Offer['id'], {
  price: number;
  units: number;
  compareAt?: number;
  save?: number;
}> = {
  // Multi-pack savings are measured against buying single Complete Systems at $49.95.
  single: {
    price: 49.95,
    units: 1
  },
  trio: {
    price: 99.9,
    units: 3,
    compareAt: 149.85,
    save: 49.95
  },
  crew: {
    price: 179.9,
    units: 6,
    compareAt: 299.7,
    save: 119.8
  }
};
export function OfferSection({
  selectedOffer,
  onSelectOffer
}: OfferSectionProps) {
  const [quantities, setQuantities] = useState<Record<Offer['id'], number>>({
    single: 1,
    trio: 1,
    crew: 1
  });
  // Optional +$19.95 lifetime upgrade on a single system. Never preselected.
  const [singleLifetime, setSingleLifetime] = useState(false);
  const upsell = useUpsellFlow();
  const sale = useActiveSale();
  // Cards reached by accepting a pack suggestion skip the quantity ladder (no chained upsells).
  const [reachedBySwitch, setReachedBySwitch] = useState<Partial<Record<Offer['id'], boolean>>>({});
  // "Want free shipping?" is customer-initiated and offered once per session.
  const [shippingOfferSeen, setShippingOfferSeen] = useState(() => flowSeen('single-lifetime-shipping'));
  const [singleShippingSeen, setSingleShippingSeen] = useState(() => flowSeen('single-no-lifetime'));
  const [missingCardLink, setMissingCardLink] = useState<Offer['id'] | null>(null);
  // RX Capability Inserts per card, chosen by quantity. Always starts at 0.
  const [rxQty, setRxQty] = useState<Record<Offer['id'], number>>({
    single: 0,
    trio: 0,
    crew: 0
  });
  const [careQty, setCareQty] = useState<Record<Offer['id'], number>>({
    single: 0,
    trio: 0,
    crew: 0
  });
  const handleQuantity = (id: Offer['id'], next: number) => {
    const prev = quantities[id];
    if (next === prev) return;
    trackCustom(next > prev ? 'quantity_increased' : 'quantity_decreased', {
      pack: id,
      from: prev,
      to: next
    });
    // The lifetime add-on is a single-unit option; more units run the free-third flow instead.
    if (id === 'single' && next !== 1) setSingleLifetime(false);
    const suggestion = suggestionFor(id, next);
    if (suggestion) trackCustom('pack_switch_suggested', {
      from: id,
      to: suggestion.switchTo,
      quantity: next
    });
    setQuantities(current => ({
      ...current,
      [id]: next
    }));
  };
  const handleLifetime = (checked: boolean) => {
    setSingleLifetime(checked);
    trackCustom(checked ? 'lifetime_selected' : 'lifetime_declined', {
      stage: 'card'
    });
  };
  const switchPack = (from: Offer['id'], suggestion: PackSuggestion) => {
    const target = suggestion.switchTo;
    trackCustom('pack_switch_accepted', {
      from,
      to: target,
      quantity: quantities[from],
      target_quantity: suggestion.targetQuantity
    });
    setQuantities(current => target === 'business' ? {
      ...current,
      [from]: 1
    } : {
      ...current,
      [from]: 1,
      [target]: suggestion.targetQuantity
    });
    if (from === 'single') setSingleLifetime(false);
    const source: SelectionSource = target === 'trio' ? 'manual_quantity_three' : target === 'crew' ? 'manual_quantity_six' : 'manual_quantity_thirty';
    setSelectionSource(source);
    if (target === 'business') {
      // The business card takes the quantity and skips its ladder (reached by a switch).
      requestBusinessQuantity(suggestion.targetQuantity);
      document.getElementById('company')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      return;
    }
    setReachedBySwitch(current => ({
      ...current,
      [target]: true
    }));
    onSelectOffer(target);
    scrollToPackCta(target);
  };
  const openShippingOffer = () => {
    trackCustom('want_free_shipping_clicked', {
      total: SINGLE_WITH_LIFETIME_PRICE,
      lifetime: true
    });
    setShippingOfferSeen(true);
    onSelectOffer('single');
    upsell.start(buildFlow('single-lifetime-shipping', 'single_lifetime'));
  };

  // $49.95 single without lifetime: 3 for $99.90 with a $59.90 "only want 1?" option
  // on the same screen, then $57.90, then the standard $49.95. Once per session.
  const openSingleShippingOffer = () => {
    trackCustom('want_free_shipping_clicked', {
      total: SINGLE_PRICE,
      lifetime: false
    });
    setSingleShippingSeen(true);
    onSelectOffer('single');
    upsell.start(buildFlow('single-no-lifetime', 'single_standard'));
  };
  return <section id="offer" className="border-b border-white/10 bg-ink-900" data-id="element-896" data-mp="3mrukronwekxk">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-897" data-mp="1k4e4p1zwj20e">
        <h2 className="grit-headline text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-898" data-mp="1ouui4m3q2nmr">
          Choose your <span className="text-grit-500" data-id="element-899" data-mp="1b3zh1vzgtatq">loadout.</span>
        </h2>
        <p className="mt-4 text-lg font-medium text-steel-300 sm:text-xl" data-id="element-900" data-mp="1b1msgzz7c27p">
          Ready-made Complete Systems, all with free U.S. shipping.{' '}
          <a href="#build-your-x1" className="font-semibold text-grit-500 underline-offset-4 hover:underline" data-id="element-901" data-mp="22vovlj5mnesa">
            Want only what you need? Build your own from $29.95 ↓
          </a>
        </p>

        <div className="mt-6 border-2 gold-edge bg-[#1a1408] p-5 sm:p-6" data-id="element-902" data-mp="1tsw4bfwg332g">
          <p className="flex flex-wrap items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#f2c94c]" data-id="element-903" data-mp="1ysipcbiilnlt">
            <InfinityIcon className="h-4 w-4" aria-hidden="true" data-id="element-904" data-mp="idzcxzcsg1rx" />
            {warrantyCopy.value} · Free with every multi-pack
          </p>
          <p className="grit-headline mt-2 text-[clamp(1.75rem,5vw,3rem)] font-extrabold uppercase gold-text" data-id="element-905" data-mp="nbir6z9hkejv">
            {warrantyCopy.title}
          </p>
          <p className="mt-2 max-w-3xl text-[15px] font-semibold text-steel-200 sm:text-lg" data-id="element-906" data-mp="auhqad48yvr5">
            {warrantyCopy.tagline} Break it, crack it, scratch a lens and we send a replacement. You never buy work
            glasses or a headlamp again. Included on Buy 2 Get 1 Free, Buy 4 Get 2 Free and the Crew Pack. A single
            system carries 1-year coverage, or add Lifetime for $19.95.{' '}
            <Link to="/legal/guarantee" className="text-[13px] font-medium text-steel-400 underline" data-id="element-907" data-mp="3ne936uwir3xk">
              Terms apply.
            </Link>
          </p>
        </div>

        <ul className="mt-10 flex flex-col gap-6 lg:grid lg:grid-cols-3 lg:items-start" data-id="element-908" data-mp="156kazcnq77h">
          {offers.map(offer => {
          const isSelected = selectedOffer === offer.id;
          const qty = quantities[offer.id];
          const math = packMath[offer.id];
          const withLifetime = offer.id === 'single' && singleLifetime && qty === 1;
          const orderTotal = withLifetime ? SINGLE_WITH_LIFETIME_PRICE : math.price * qty;
          const orderUnits = math.units * qty;
          return <li key={offer.id} id={`pack-${offer.id}`} className="h-full scroll-mt-28" data-id="element-909" data-mp="2szgggmk3h786">
                <article className={`relative flex h-full flex-col border bg-ink-950 p-6 transition-colors duration-150 ease-grit lg:p-7 ${isSelected ? 'border-grit-500' : 'border-white/12 hover:border-white/30'} ${offer.featured ? 'lg:-mt-4 lg:pb-9' : ''}`} data-id="element-910" data-mp="321ze8s90wtnk">
                  <p className="-mx-6 -mt-6 mb-5 flex items-center justify-between gap-3 bg-grit-500 px-6 py-2.5 text-ink-950 lg:-mx-7 lg:-mt-7 lg:px-7" data-id="element-911" data-mp="3gpasx30i4trw">
                    <span className="font-display text-2xl font-extrabold uppercase leading-none" data-id="element-912" data-mp="3pr0uh1o3l93e">
                      {math.save ? `Save ${money(math.save).replace('.00', '')}` : 'Ready to work'}
                    </span>
                    <span className="text-right text-[10px] font-extrabold uppercase leading-tight tracking-[0.16em]" data-id="element-913" data-mp="1a23n8fypt0rs">
                      {sale.name}
                      {math.units > 1 && <span className="block" data-id="element-914" data-mp="104fhkuzkm8uu">+ free units</span>}
                    </span>
                  </p>

                  {offer.image ? <>
                      {/* The pack artwork already carries the badge, the offer, the unit count and the per-X1 price. */}
                      <h3 className="sr-only" data-id="element-915" data-mp="1o1uywa1re52m">
                        {[offer.eyebrow, offer.kicker, offer.name].filter(Boolean).join(' · ')}
                      </h3>
                      <img src={offer.image} alt={offer.imageAlt ?? offer.name} loading="lazy" width={1122} height={1402} className="h-auto w-full border border-white/10" data-id="element-916" data-mp="1j0xio30b7l3r" />
                    </> : <>
                      {offer.eyebrow && <p className={`mb-3 inline-flex self-start px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${offer.featured ? 'bg-grit-500 text-ink-950' : 'border border-grit-500/60 text-grit-500'}`} data-id="element-917" data-mp="99w24vim8vvs">
                          {offer.eyebrow}
                        </p>}
                      {offer.kicker && <p className="text-xs font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-918" data-mp="n10wpj74mxz9">{offer.kicker}</p>}
                      <h3 className={`mt-1 font-display font-extrabold uppercase leading-none text-offwhite ${offer.featured ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`} data-id="element-919" data-mp="111tuccsq696l">
                        {offer.name}
                      </h3>
                      <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-920" data-mp="1kkhjs6nyecfi">{offer.units}</p>
                    </>}

                  <div className="mt-6 flex flex-wrap items-end gap-x-3 gap-y-1" data-id="element-921" data-mp="1bkukprip5ipm">
                    <span className={`font-display font-extrabold leading-none text-offwhite ${offer.featured ? 'text-6xl sm:text-7xl' : 'text-5xl'}`} data-id="element-922" data-mp="wwrb885g3ow2">
                      {money(orderTotal)}
                    </span>
                    {math.compareAt && !withLifetime && <span className="pb-1 text-base text-steel-500 line-through" data-id="element-923" data-mp="dy586nqdbnpi">
                        {money(math.compareAt * qty)}
                      </span>}
                  </div>

                  {withLifetime ? <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-[#ffe79a]" data-id="element-924" data-mp="2kn78xhx54p7f">
                      {money(math.price)} + {money(LIFETIME_UPGRADE_PRICE)} lifetime
                    </p> : math.save && <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-grit-500" data-id="element-925" data-mp="26vsg6jdfl9n6">
                        You save {money(math.save * qty)}
                      </p>}
                  {offer.perUnit && !offer.image && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-926" data-mp="2uk23xqu7g7gy">
                      {offer.perUnit}
                    </p>}
                  {qty > 1 && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-927" data-mp="1j6g51dyjmri8">
                      {qty} packs · {orderUnits} X1 total
                    </p>}

                  {offer.highlight && !offer.image && <p className="mt-4 bg-grit-500 px-4 py-3 font-display text-2xl font-extrabold uppercase leading-none tracking-wide text-ink-950 sm:text-3xl" data-id="element-928" data-mp="19188sh01fj8q">
                      {offer.highlight}
                    </p>}
                  {offer.runtime && <p className="mt-3 font-display text-lg font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-929" data-mp="1it4a00xf7ph6">
                      {offer.runtime}
                    </p>}

                  <p className="mt-4 text-[13px] font-semibold uppercase tracking-wide text-steel-300" data-id="element-930" data-mp="1wr2k2u4pkzqv">
                    {money(pricing.separateValue)} total value per X1
                  </p>

                  <div className="mt-5 border-t border-white/10 pt-5" data-id="element-931" data-mp="2d1apfbj8ucj">
                    {/* Everyday items are pictured in the artwork's icon strip, so the list lives one tap away. */}
                    <details className="group" data-id="element-932" data-mp="7dz3smy2z8st">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-300 transition-colors duration-150 ease-grit hover:text-offwhite [&::-webkit-details-marker]:hidden" data-id="element-933" data-mp="1xs6pmsawnuiw">
                        Full list of what&apos;s in the box
                        <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-grit group-open:rotate-180" aria-hidden="true" data-id="element-934" data-mp="21vmjxhzi5isf" />
                      </summary>
                      <ul className="mt-3 grid gap-2 text-[14px] font-medium text-steel-200" data-id="element-935" data-mp="1nvbqq8apzs43">
                        {offer.includes.filter(line => !/1-year|warranty|mystery gift/i.test(line)).map(line => <li key={line} className="flex items-start gap-2.5" data-id="element-936" data-mp="qfo9b9ooeop3">
                              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-937" data-mp="qdtv0e7wtutz" />
                              {line}
                            </li>)}
                      </ul>
                    </details>
                    <ul className="mt-4 grid gap-2 text-[15px] font-medium text-offwhite" data-id="element-938" data-mp="2t3mor1umt1ye">
                      {offer.includes.filter(line => /1-year|warranty|mystery gift/i.test(line)).map(line => {
                    if (/1-year/i.test(line)) {
                      return <li key={line} className="mt-1 grid gap-2" data-id="element-939" data-mp="3praz2xogbq7e">
                              <span className="flex items-start gap-2.5" data-id="element-940" data-mp="3btmbhi3024yn">
                                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-941" data-mp="3grxkjqlh0bft" />
                                {line}
                              </span>
                              {qty === 1 && <LifetimeUpgradeOption checked={singleLifetime} onChange={handleLifetime} showShippingOffer={false} onShippingOffer={openShippingOffer} data-id="element-942" data-mp="25dsdgopynlg9" />}
                              {!singleLifetime && <button type="button" onClick={() => {
                          onSelectOffer('trio');
                          scrollToPackCta('trio');
                        }} className="group flex items-center gap-3 border gold-edge bg-[#1a1408] p-3.5 text-left transition-colors duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-943" data-mp="17iq213jnycjc">
                                <InfinityIcon className="h-7 w-7 shrink-0 text-[#f2c94c]" aria-hidden="true" data-id="element-944" data-mp="2iy4zs9jp58fi" />
                                <span data-id="element-945" data-mp="2qgw5wm74hsn5">
                                  <span className="block font-display text-base font-bold uppercase leading-tight text-[#ffe79a]" data-id="element-946" data-mp="2qftdg9u5ji54">
                                    Want it covered for life?
                                  </span>
                                  <span className="mt-0.5 block text-[13px] font-semibold text-steel-300" data-id="element-947" data-mp="356k43ek0cb6n">
                                    For just $49.95 more: 2 extra X1 plus the Lifetime Replacement Warranty on all
                                    3. Upgrade to Buy 2, Get 1 Free →
                                  </span>
                                </span>
                              </button>}
                            </li>;
                    }
                    if (/warranty/i.test(line)) {
                      return <li key={line} className="mt-1" data-id="element-948" data-mp="ru4zqndh3sx">
                              <Link to="/legal/guarantee" className="group flex items-center gap-4 border gold-edge bg-[#1a1408] p-4 transition-[background-color,box-shadow] duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-949" data-mp="mu5gm23v5i37">
                                <GoldMedal size={86} data-id="element-950" data-mp="rr5omik9wuwy" />
                                <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-951" data-mp="15jlurs8h1923">
                                  {math.units > 1 ? `All ${math.units} covered for life.` : 'Covered for life.'}
                                  <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-952" data-mp="1fgqgxs2bh4iw">
                                    {warrantyCopy.title}. {warrantyCopy.tagline} {warrantyCopy.value},{' '}
                                    {warrantyCopy.status.toLowerCase()}.
                                  </span>
                                </p>
                              </Link>
                            </li>;
                    }
                    if (/mystery gift/i.test(line)) {
                      return <li key={line} className="mt-1 border border-grit-500/60 bg-grit-500/10 p-3.5 font-display text-lg font-bold uppercase leading-tight tracking-wide text-grit-500" data-id="element-953" data-mp="1i1eq3lvkmjji">
                              <span className="flex items-center gap-2" data-id="element-954" data-mp="1d5hchnbozraf">
                                <GiftIcon className="h-4 w-4" aria-hidden="true" data-id="element-955" data-mp="z5onpxjq7l53" />
                                Free mystery gift
                              </span>
                              <span className="mt-1 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-956" data-mp="2thwecyp0lfv2">
                                There&apos;s something else in the box. No clues. No extra charge. While supplies
                                last.
                              </span>
                            </li>;
                    }
                    return <li key={line} className="flex items-start gap-2.5" data-id="element-957" data-mp="3t2ad321lwhrh">
                            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-958" data-mp="weqqdwpbftf" />
                            {line}
                          </li>;
                  })}
                    </ul>
                    {offer.splitNote && <div className="mt-4 border-l-2 border-grit-500 bg-white/[0.03] px-4 py-3" data-id="element-959" data-mp="fmvtc0l2obrw">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-960" data-mp="kke5s0yat94n">
                          {offer.splitNote.title}
                        </p>
                        <p className="mt-1 text-[14px] font-medium text-steel-200" data-id="element-961" data-mp="35b4w2jjxjjfl">{offer.splitNote.body}</p>
                        <p className="mt-1 font-display text-base font-bold uppercase tracking-wide text-offwhite" data-id="element-962" data-mp="2wturegmzl8ys">
                          {offer.splitNote.each}
                        </p>
                      </div>}
                    <PrescriptionBadge className="mt-3" compact data-id="element-963" data-mp="36s1vw0039yjw" />
                    <ul className="mt-4 grid gap-1 border-t border-white/5 pt-4 text-sm text-steel-400" data-id="element-964" data-mp="31t7dgz61ac0b">
                      {offer.supporting.map(line => <li key={line} data-id="element-965" data-mp="2e1js0wrjjt5f">{line}</li>)}
                    </ul>
                  </div>

                  <div className="mt-auto pt-7" data-id="element-966" data-mp="2nu7pltqci157">
                    <RxQuantityControl value={rxQty[offer.id]} max={orderUnits} onChange={next => {
                  trackCustom('multipack_rx_quantity', {
                    pack: offer.id,
                    quantity: next
                  });
                  setRxQty(current => ({
                    ...current,
                    [offer.id]: next
                  }));
                }} className="mb-3" data-id="element-967" data-mp="2nv0o67f5hkc9" />
                    <CareKitControl value={careQty[offer.id]} max={orderUnits} onChange={next => {
                  trackCustom(next > careQty[offer.id] ? 'care_kit_added' : 'care_kit_removed', {
                    pack: offer.id,
                    quantity: next
                  });
                  setCareQty(current => ({
                    ...current,
                    [offer.id]: next
                  }));
                }} className="mb-4" data-id="element-968" data-mp="3vfo4huoex6y0" />
                    <QuantityStepper value={qty} onChange={next => handleQuantity(offer.id, next)} label={`${offer.name} quantity`} className="mb-4" data-id="element-969" data-mp="3ghlxtbm345cj" />
                    {(() => {
                  const suggestion = suggestionFor(offer.id, qty);
                  if (!suggestion) return null;
                  return <div role="status" className="mb-4 border border-grit-500/70 bg-grit-500/10 p-3.5" data-id="element-970" data-mp="15w38225zrpgw">
                          <p className="text-[13px] font-semibold leading-snug text-steel-200" data-id="element-971" data-mp="1154sx5wqoxf8">{suggestion.message}</p>
                          {!suggestion.replacesCheckout && <button type="button" onClick={() => switchPack(offer.id, suggestion)} className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-grit-500 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-972" data-mp="x1ylhuxw5076">
                              {suggestion.action}
                              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-973" data-mp="2cifl7pj86m90" />
                            </button>}
                        </div>;
                })()}
                    {(() => {
                  const suggestion = suggestionFor(offer.id, qty);
                  if (suggestion?.replacesCheckout) {
                    // The card's own checkout would charge more for the same units: the switch replaces it.
                    return <button id={`pack-${offer.id}-cta`} type="button" onClick={() => switchPack(offer.id, suggestion)} className="flex w-full scroll-mb-28 items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-974" data-mp="1wvwwl76wpmy4">
                            {suggestion.action}
                            <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-975" data-mp="zc6lh1olibet" />
                          </button>;
                  }
                  const ctaClass = `flex w-full scroll-mb-28 items-center justify-center gap-2 px-5 py-4 text-center font-display font-extrabold uppercase tracking-wide transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${offer.featured ? 'bg-grit-500 text-ink-950 hover:bg-grit-400 text-2xl' : 'border-2 border-grit-500 text-grit-500 hover:bg-grit-500 hover:text-ink-950 text-xl'}`;
                  const ctaLabel = withLifetime ? `Get the X1 + Lifetime — ${money(orderTotal)}` : qty > 1 ? `Get ${orderUnits} X1 for ${money(orderTotal)}` : offer.cta;
                  // 1 X1 + Lifetime is its own Shopify product ($69.90, shipping at checkout).
                  const linkKey = withLifetime ? 'singleX1LifetimeNormal6990' : offer.link;
                  const href = cartLink(linkKey, withLifetime ? 1 : qty);
                  return <a id={`pack-${offer.id}-cta`} href={href || '#offer'} data-buy-intent="true" onClick={event => {
                    // RX inserts chosen on this card ride along with whatever this checkout ends on.
                    setPendingRx(Math.min(rxQty[offer.id], orderUnits));
                    setPendingCare(Math.min(careQty[offer.id], orderUnits));
                    onSelectOffer(offer.id);
                    trackCustom(packEvents[offer.id].addToCart, {
                      quantity: qty,
                      value: orderTotal
                    });
                    trackCustom('product_selected', {
                      pack: offer.id,
                      quantity: qty,
                      lifetime: withLifetime
                    });
                    if (offer.id === 'single' && qty === 1 && !withLifetime) {
                      trackCustom('lifetime_declined', {
                        stage: 'checkout'
                      });
                    }
                    // At most one negotiation flow per checkout, each flow once per session.
                    const next = checkoutFlow(offer.id, qty, {
                      lifetime: withLifetime,
                      reachedBySwitch: reachedBySwitch[offer.id]
                    });
                    if (next && !flowSeen(next.kind)) {
                      event.preventDefault();
                      if (next.kind === 'single-no-lifetime') setSingleShippingSeen(true);
                      if (next.kind === 'single-lifetime-shipping') setShippingOfferSeen(true);
                      upsell.start(buildFlow(next.kind, next.source));
                      return;
                    }
                    setSelectionSource(offer.id === 'single' ? withLifetime ? 'single_lifetime' : qty === 2 ? 'two_unit_selection' : 'single_standard' : offer.id === 'trio' ? reachedBySwitch.trio ? 'manual_quantity_three' : 'direct_three_pack' : reachedBySwitch.crew ? 'manual_quantity_six' : 'direct_six_pack');
                    if (!href) {
                      event.preventDefault();
                      setMissingCardLink(offer.id);
                      console.warn(`[GritVue] PRODUCT_LINKS.${linkKey} is empty.`);
                      return;
                    }
                    trackCustom('upsell_flow_completed', {
                      final_product: linkKey,
                      value: orderTotal,
                      quantity: qty,
                      result: 'direct'
                    });
                    trackShopifyAddToCart(event, {
                      content_name: withLifetime ? 'GritVue X1 Complete System + Lifetime Replacement Warranty' : offer.name,
                      content_ids: [variantIdOf(PRODUCT_LINKS[linkKey])],
                      quantity: qty,
                      value: Number(orderTotal.toFixed(2))
                    });
                  }} className={ctaClass} data-id="element-976" data-mp="1cdy3m5wssdbi">
                          {ctaLabel}
                        </a>;
                })()}
                    {missingCardLink === offer.id && <p role="alert" className="mt-3 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-977" data-mp="o7evpl3yw76x">
                        This checkout link isn&apos;t connected yet. Please try again shortly or email
                        support@gritvuegear.com.
                      </p>}
                    <UspsBadge className="mt-3" note={offer.note} data-id="element-978" data-mp="a8nominivlsa" />
                    <SecureCheckoutNote className="mt-4" data-id="element-979" data-mp="17ukyxsrr0kt5" />
                    {offer.badge && <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-grit-500" data-id="element-980" data-mp="12x2yyrvplfxa">
                        {offer.badge}
                      </p>}
                    {offer.footnote && <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-981" data-mp="1qlvoo6lqmrjg">{offer.footnote}</p>}
                  </div>
                </article>
              </li>;
        })}
        </ul>

        <IncludedGear data-id="element-982" data-mp="1t73hpmo7d50q" />

        <CompanyPack data-id="element-983" data-mp="1t7uznuovn48j" />

        <UpsellModal step={upsell.step} confirmation={upsell.confirmation} onContinue={upsell.continueToCheckout} onFollowUp={upsell.followUp} routing={upsell.routing} missingLink={upsell.missingLink} onAccept={upsell.accept} onAcceptAlternate={upsell.acceptAlternate} onDecline={upsell.decline} onClose={upsell.close} data-id="element-984" data-mp="1t8eg16yvpkkg" />

        <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-985" data-mp="2h3zav3a5dgm1">
          <div data-id="element-986" data-mp="2lwad4csd1ehr">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-987" data-mp="2lvhbtrc67h70">Secure checkout</p>
            <div className="mt-2" data-id="element-988" data-mp="2qtan2ysbz1e5">
              <PaymentLogos data-id="element-989" data-mp="2vtf2smlezs3b" />
            </div>
          </div>
          <p className="max-w-md text-[13px] text-steel-400" data-id="element-990" data-mp="2vuztg14lyqvw">
            30-day returns with free return shipping · Free U.S. shipping on every order · Taxes, if
            applicable, calculated at checkout ·{' '}
            <Link to="/legal/shipping" className="font-semibold text-grit-500 hover:underline" data-id="element-991" data-mp="10nmhpvdi67yi">
              Shipping &amp; returns
            </Link>
          </p>
        </div>
      </div>
    </section>;
}